import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, Check, X, Tv, Search, Film, Play, Clock, ArrowUpDown } from "lucide-react";
import { useAuth } from "@/lib/auth";

function Avatar({ user, size = 36 }) {
  return (
    <div className="rounded-full flex items-center justify-center shrink-0" style={{ width: size, height: size, backgroundColor: user.avatarColor || "#00D9FF", fontSize: size * 0.5 }}>
      {user.avatarEmoji || "🎬"}
    </div>
  );
}

export default function FriendsPanel({ onRewatch, initialTab }) {
  const navigate = useNavigate();
  const { friends, incoming, refreshFriends, authFetch } = useAuth();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [tab, setTab] = useState(initialTab || "friends");
  const [history, setHistory] = useState([]);
  const [sortBy, setSortBy] = useState("date");
  const [histQ, setHistQ] = useState("");

  useEffect(() => {
    if (tab !== "history") return;
    authFetch("/history").then((d) => setHistory(d.items || [])).catch(() => {});
  }, [tab, authFetch]);

  const doSearch = async (val) => {
    setQ(val);
    if (val.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const d = await authFetch(`/users/search?q=${encodeURIComponent(val.trim())}`);
      setResults(d.users || []);
    } catch (e) { /* search failed */ } finally { setSearching(false); }
  };

  const sendRequest = async (username) => {
    try {
      const d = await authFetch("/friends/request", { method: "POST", body: JSON.stringify({ username }) });
      toast.success(d.status === "friends" ? "You're now friends!" : "Friend request sent");
      doSearch(q); refreshFriends();
    } catch (e) { toast.error(e.message); }
  };

  const respond = async (requestId, accept) => {
    try {
      await authFetch("/friends/respond", { method: "POST", body: JSON.stringify({ requestId, accept }) });
      refreshFriends();
    } catch (e) { toast.error(e.message); }
  };

  const deleteHistoryItem = async (id) => {
    try { await authFetch(`/history/${id}`, { method: "DELETE" }); setHistory((h) => h.filter((x) => x.id !== id)); }
    catch (e) { toast.error(e.message); }
  };

  const clearHistory = async () => {
    try { await authFetch("/history", { method: "DELETE" }); setHistory([]); toast("History cleared"); }
    catch (e) { toast.error(e.message); }
  };

  const STREAK_TIERS = [{ n: 100, e: "👑" }, { n: 50, e: "🏆" }, { n: 25, e: "🥇" }, { n: 10, e: "🥈" }, { n: 5, e: "🥉" }];
  const streakEmoji = (c) => (STREAK_TIERS.find((t) => c >= t.n) || {}).e || "";

  const fmtDur = (s) => {
    if (!s || s < 1) return null;
    const h = Math.floor(s / 3600), m = Math.round((s % 3600) / 60);
    if (h) return `${h}h ${m}m watched`;
    if (m) return `${m}m watched`;
    return `${s}s watched`;
  };
  const fmtDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) +
        " · " + d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    } catch { return ""; }
  };
  const SORTS = [{ k: "date", label: "Newest" }, { k: "duration", label: "Duration" }, { k: "friend", label: "Friend" }, { k: "source", label: "Source" }];
  const shownHistory = history
    .filter((h) => {
      const q = histQ.trim().toLowerCase();
      if (!q) return true;
      return (h.title || "").toLowerCase().includes(q) ||
        (h.source || "").toLowerCase().includes(q) ||
        (h.with || []).some((w) => (w.username || "").toLowerCase().includes(q));
    })
    .slice()
    .sort((a, b) => {
      if (sortBy === "duration") return (b.durationSec || 0) - (a.durationSec || 0);
      if (sortBy === "source") return (a.source || "").localeCompare(b.source || "");
      if (sortBy === "friend") return (((a.with || [])[0] || {}).username || "").localeCompare(((b.with || [])[0] || {}).username || "");
      return (b.watchedAt || "").localeCompare(a.watchedAt || "");
    });

  return (
    <div data-testid="friends-panel" className="space-y-4">
      <div className="flex gap-1 bg-[#0F1636] rounded-full p-1">
        <button data-testid="tab-friends" onClick={() => setTab("friends")} className={`flex-1 h-8 rounded-full text-xs font-semibold transition-colors ${tab === "friends" ? "bg-[#00D9FF] text-navy-ink" : "text-[#A0A0AB]"}`}>Friends</button>
        <button data-testid="tab-history" onClick={() => setTab("history")} className={`flex-1 h-8 rounded-full text-xs font-semibold transition-colors ${tab === "history" ? "bg-[#00D9FF] text-navy-ink" : "text-[#A0A0AB]"}`}>History</button>
      </div>

      {tab === "friends" && (<>
      {/* add friend */}
      <div>
        <div className="flex items-center gap-2 h-10 bg-[#0F1636] border border-white/10 rounded-xl px-3 focus-within:border-[#00D9FF]">
          <Search className="w-4 h-4 text-[#71717A]" />
          <input data-testid="friend-search-input" value={q} onChange={(e) => doSearch(e.target.value)} placeholder="Add friends by @username" className="flex-1 bg-transparent outline-none text-sm text-[#F1F1F5] placeholder:text-[#71717A]" />
        </div>
        {results.length > 0 && (
          <div className="mt-2 space-y-1" data-testid="friend-search-results">
            {results.map((u) => (
              <div key={u.id} className="flex items-center gap-2 bg-white/5 rounded-lg p-1.5">
                <Avatar user={u} size={30} />
                <span className="text-sm text-[#F1F1F5] flex-1 truncate">@{u.username} <span className="text-xs">{u.flag}</span></span>
                {u.relation === "friend" ? <span className="text-[11px] text-emerald-400 pr-2">Friends</span>
                  : u.relation === "requested" ? <span className="text-[11px] text-[#71717A] pr-2">Requested</span>
                  : <button data-testid={`add-friend-${u.username}`} onClick={() => sendRequest(u.username)} className="w-7 h-7 rounded-full bg-[#00D9FF] text-navy-ink flex items-center justify-center"><UserPlus className="w-3.5 h-3.5" /></button>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* incoming requests */}
      {incoming.length > 0 && (
        <div data-testid="friend-requests">
          <p className="text-[10px] uppercase tracking-wider text-[#71717A] mb-1.5">Requests</p>
          <div className="space-y-1">
            {incoming.map((r) => (
              <div key={r.requestId} className="flex items-center gap-2 bg-white/5 rounded-lg p-1.5">
                <Avatar user={r.user} size={30} />
                <span className="text-sm text-[#F1F1F5] flex-1 truncate">@{r.user.username}</span>
                <button data-testid={`accept-${r.user.username}`} onClick={() => respond(r.requestId, true)} className="w-7 h-7 rounded-full bg-emerald-500 text-white flex items-center justify-center"><Check className="w-3.5 h-3.5" /></button>
                <button data-testid={`decline-${r.user.username}`} onClick={() => respond(r.requestId, false)} className="w-7 h-7 rounded-full bg-white/10 text-[#A0A0AB] flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* friends list */}
      <div>
        <p className="text-[10px] uppercase tracking-wider text-[#71717A] mb-1.5">Friends ({friends.length})</p>
        {friends.length === 0 ? (
          <p className="text-xs text-[#71717A] py-2">No friends yet — search a @username above.</p>
        ) : (
          <div className="space-y-1" data-testid="friends-list">
            {friends.map((f) => (
              <div key={f.id} className="flex items-center gap-2 bg-white/5 rounded-lg p-1.5">
                <div className="relative">
                  <Avatar user={f} size={32} />
                  <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[#0A0E27] ${f.online ? "bg-emerald-400" : "bg-[#3a3a42]"}`} data-testid={`status-${f.username}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-[#F1F1F5] flex items-center gap-1 truncate">@{f.username} <span className="text-xs">{f.flag}</span></span>
                  {f.watchedTogether > 0 && (
                    <span data-testid={`watched-together-${f.username}`} className="flex items-center gap-1 text-[10px] text-[#00D9FF] font-semibold mt-0.5">
                      {streakEmoji(f.watchedTogether) && <span data-testid={`streak-badge-${f.username}`} className="text-xs">{streakEmoji(f.watchedTogether)}</span>}
                      <Film className="w-2.5 h-2.5" /> {f.watchedTogether} watched together
                    </span>
                  )}
                  {f.inRoom && f.watching && (
                    <span data-testid={`watching-${f.username}`} className="flex items-center gap-1 text-[10px] text-[#FF006E] font-semibold mt-0.5 truncate" title={f.watching}>
                      <Tv className="w-2.5 h-2.5 shrink-0" /> <span className="truncate">Watching “{f.watching}”</span>
                    </span>
                  )}
                </div>
                {f.inRoom ? (
                  <button data-testid={`join-friend-${f.username}`} onClick={() => navigate(`/room/${f.inRoom}`)} className="flex items-center gap-1 text-[11px] font-semibold bg-[#00D9FF]/20 text-[#00D9FF] rounded-full px-2 py-1 hover:bg-[#00D9FF]/30">
                    <Tv className="w-3 h-3" /> Join
                  </button>
                ) : f.online ? <span className="text-[11px] text-emerald-400 pr-1">online</span> : <span className="text-[11px] text-[#71717A] pr-1">offline</span>}
              </div>
            ))}
          </div>
        )}
      </div>
      </>)}

      {tab === "history" && (
        <div data-testid="history-panel">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] uppercase tracking-wider text-[#71717A]">Watch history</p>
            {history.length > 0 && (
              <button data-testid="clear-history-button" onClick={clearHistory} className="text-[10px] uppercase tracking-wider text-[#71717A] hover:text-[#FF006E] transition-colors">Clear all</button>
            )}
          </div>

          {history.length > 0 && (
            <div className="space-y-2 mb-3">
              <div className="flex items-center gap-2 h-9 bg-[#0F1636] border border-white/10 rounded-lg px-3 focus-within:border-[#00D9FF]">
                <Search className="w-3.5 h-3.5 text-[#71717A]" />
                <input data-testid="history-search-input" value={histQ} onChange={(e) => setHistQ(e.target.value)} placeholder="Search title, friend or source…" className="flex-1 bg-transparent outline-none text-xs text-[#F1F1F5] placeholder:text-[#71717A]" />
              </div>
              <div className="flex items-center gap-1.5">
                <ArrowUpDown className="w-3 h-3 text-[#71717A] shrink-0" />
                {SORTS.map((s) => (
                  <button
                    key={s.k}
                    data-testid={`history-sort-${s.k}`}
                    onClick={() => setSortBy(s.k)}
                    className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-full transition-colors ${sortBy === s.k ? "bg-[#00D9FF] text-navy-ink" : "bg-white/5 text-[#A0A0AB] hover:bg-white/10"}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {history.length === 0 ? (
            <p className="text-xs text-[#71717A] py-2">Nothing yet — watch something in a room and it shows up here.</p>
          ) : shownHistory.length === 0 ? (
            <p className="text-xs text-[#71717A] py-2">No sessions match “{histQ}”.</p>
          ) : (
            <div className="space-y-1.5" data-testid="history-list">
              {shownHistory.map((h) => (
                <div key={h.id} className="group flex items-start gap-2 bg-white/5 rounded-lg p-2.5" data-testid="history-item">
                  <button
                    data-testid={`rewatch-${h.id}`}
                    onClick={() => onRewatch && onRewatch(h.url)}
                    className="flex-1 min-w-0 text-left"
                    title="Rewatch in a new room"
                  >
                    <div className="flex items-center gap-2">
                      <Play className="w-3.5 h-3.5 text-[#00D9FF] shrink-0 fill-[#00D9FF]" />
                      <span className="text-sm text-[#F1F1F5] truncate group-hover:text-[#00D9FF] transition-colors" title={h.title}>{h.title}</span>
                    </div>
                    {(h.with && h.with.length > 0) && (
                      <p className="text-[11px] text-[#A0A0AB] mt-1 pl-5 truncate">with {h.with.map((w) => `${w.avatarEmoji} @${w.username}`).join(", ")}</p>
                    )}
                    <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mt-1 pl-5">
                      <span className="text-[10px] text-[#71717A]" data-testid={`history-date-${h.id}`}>{fmtDate(h.watchedAt)}</span>
                      {fmtDur(h.durationSec) && (
                        <span className="flex items-center gap-0.5 text-[10px] text-[#9D4EDD] font-semibold" data-testid={`history-duration-${h.id}`}>
                          <Clock className="w-2.5 h-2.5" /> {fmtDur(h.durationSec)}
                        </span>
                      )}
                      {h.source && (
                        <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#00D9FF]/12 text-[#00D9FF] border border-[#00D9FF]/30" data-testid={`history-source-${h.id}`}>📍 {h.source}</span>
                      )}
                    </div>
                  </button>
                  <button data-testid={`delete-history-${h.id}`} onClick={() => deleteHistoryItem(h.id)} className="shrink-0 text-[#71717A] hover:text-[#FF006E] transition-colors" title="Remove">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
